import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { Animated, PanResponder, Dimensions } from 'react-native';

const { width: W, height: H } = Dimensions.get('window');

const LiveSessionContext = createContext(null);

/**
 * LiveSessionProvider
 *
 * Holds global mini-session state so the PiP overlay can float above
 * all navigation screens after the user minimizes a live room.
 *
 * Usage:
 *   - Call `minimizeSession(meta)` from MentorLiveRoomScreen when the
 *     user taps "Minimize".  meta = { topic, mentorName, participantCount,
 *     isMicEnabled, onToggleMic, onRestore }.
 *   - Call `clearSession()` when the session fully ends.
 */
export function LiveSessionProvider({ children }) {
    const [session, setSession] = useState(null); // null = not minimized

    // ── Draggable PiP ──────────────────────────────────────────────────────────
    const pipPan = useRef(new Animated.ValueXY({ x: W - 185, y: H - 260 })).current;
    const pipPanResponder = useRef(
        PanResponder.create({
            onMoveShouldSetPanResponder: () => true,
            onPanResponderGrant: () => {
                pipPan.setOffset({ x: pipPan.x._value, y: pipPan.y._value });
            },
            onPanResponderMove: Animated.event(
                [null, { dx: pipPan.x, dy: pipPan.y }],
                { useNativeDriver: false }
            ),
            onPanResponderRelease: () => pipPan.flattenOffset(),
        })
    ).current;

    const minimizeSession = useCallback((meta) => {
        setSession(meta);
    }, []);

    const clearSession = useCallback(() => {
        setSession(null);
    }, []);

    return (
        <LiveSessionContext.Provider value={{ session, minimizeSession, clearSession, pipPan, pipPanResponder }}>
            {children}
        </LiveSessionContext.Provider>
    );
}

export function useLiveSession() {
    const ctx = useContext(LiveSessionContext);
    if (!ctx) throw new Error('useLiveSession must be used inside LiveSessionProvider');
    return ctx;
}
